import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// ── Cart Store ─────────────────────────────────────────────────────
// For guests: local Zustand only (persisted to localStorage)
// For auth users: Zustand + background server sync on every mutation
// cartService is imported lazily to avoid circular deps at module init
async function syncAdd(productId, qty) {
  try {
    const { cartService } = await import('../services/api.js')
    await cartService.addItem(productId, qty)
  } catch (e) {
    console.warn('[cart] server sync failed — local cart still updated', e.message)
  }
}

async function syncRemove(cartItemId) {
  try {
    const { cartService } = await import('../services/api.js')
    await cartService.removeItem(cartItemId)
  } catch (e) {
    console.warn('[cart] server remove failed', e.message)
  }
}

async function syncUpdate(cartItemId, qty) {
  try {
    const { cartService } = await import('../services/api.js')
    await cartService.updateItem(cartItemId, qty)
  } catch (e) {
    console.warn('[cart] server update failed', e.message)
  }
}

export const useCartStore = create(
  persist(
    (set, get) => ({
      items: [],
      ownerId: null,   // user id this cart belongs to

      // Call on login — clears cart if it belonged to a different user
      claimCart: (userId) => {
        const current = get().ownerId
        if (current !== null && current !== userId) {
          set({ items: [], ownerId: userId })
        } else {
          set({ ownerId: userId })
        }
      },

      // Always updates local state immediately; if authenticated, fires server sync
      addItem: (product, qty = 1) => {
        const items = get().items
        const existing = items.find(i => i.id === product.id)
        if (existing) {
          set({ items: items.map(i => i.id === product.id ? { ...i, qty: i.qty + qty } : i) })
        } else {
          set({ items: [...items, { ...product, qty }] })
        }

        // Server sync for authenticated users
        const auth = useAuthStore.getState()
        if (auth.isAuthenticated) {
          syncAdd(product.id, qty)
        }
      },

      removeItem: (id) => {
        const item = get().items.find(i => i.id === id)
        set({ items: get().items.filter(i => i.id !== id) })

        const auth = useAuthStore.getState()
        if (auth.isAuthenticated && item?.cartItemId) {
          syncRemove(item.cartItemId)
        }
      },

      updateQty: (id, qty) => {
        if (qty <= 0) { get().removeItem(id); return }
        const item = get().items.find(i => i.id === id)
        set({ items: get().items.map(i => i.id === id ? { ...i, qty } : i) })

        const auth = useAuthStore.getState()
        if (auth.isAuthenticated && item?.cartItemId) {
          syncUpdate(item.cartItemId, qty)
        }
      },

      clearCart: () => set({ items: [] }),

      // Zero out all quantities after loading a template — user must enter their own qtys
      zeroAllQuantities: () => {
        const zeroed = get().items.map(i => ({ ...i, qty: 0 }))
        set({ items: zeroed })
        const auth = useAuthStore.getState()
        if (auth.isAuthenticated) {
          zeroed.forEach(i => { if (i.cartItemId) syncUpdate(i.cartItemId, 0) })
        }
      },

      // Merge server cart into local on login (call this after login)
      mergeServerCart: (serverItems) => {
        if (!serverItems?.length) return
        // Server now returns full product details - build fresh from server data
        const merged = serverItems.map(si => ({
          id:          si.product_id,
          name:        si.product?.description || si.product?.product_name || `Product #${si.product_id}`,
          price:       si.unit_price || si.price || si.product?.inclprice2 || 0,
          image:       si.product?.primary_image
                         ? `/MappedData/${si.product.primary_image}`
                         : si.product?.stock_code
                           ? `/MappedData/${si.product.stock_code}/1.webp`
                           : null,
          qty:         si.quantity,
          cartItemId:  si.id,
          stock_code:  si.product?.stock_code || null,
          brand:       si.product?.brand || null,
          product:     si.product || null,
        }))
        set({ items: merged })
      },

      get total() { return get().items.reduce((sum, i) => sum + i.price * i.qty, 0) },
      get count() { return get().items.reduce((sum, i) => sum + i.qty, 0) },
    }),
    { name: 'tonys-cart' }
  )
)

// ── Wishlist Store ─────────────────────────────────────────────────
// Local state is the source of truth for UI (fast toggles).
// Server is synced in the background — failures are silent (UX stays snappy).
async function serverWishlistAdd(productId) {
  try {
    const { wishlistService } = await import('../services/api.js')
    await wishlistService.add(productId)
  } catch (e) {
    console.warn('[wishlist] server add failed', e.message)
  }
}

async function serverWishlistRemove(productId) {
  try {
    const { wishlistService } = await import('../services/api.js')
    await wishlistService.removeByProduct(productId)
  } catch (e) {
    console.warn('[wishlist] server remove failed', e.message)
  }
}

export const useWishlistStore = create(
  persist(
    (set, get) => ({
      items: [],
      toggle: (product) => {
        const items  = get().items
        const exists = items.find(i => i.id === product.id)
        if (exists) {
          set({ items: items.filter(i => i.id !== product.id) })
          // Only call API if user is logged in (checked lazily via auth store)
          const auth = useAuthStore.getState()
          if (auth.isAuthenticated) serverWishlistRemove(product.id)
        } else {
          set({ items: [...items, product] })
          const auth = useAuthStore.getState()
          if (auth.isAuthenticated) serverWishlistAdd(product.id)
        }
      },
      has:   (id) => get().items.some(i => i.id === id),
      clear: ()   => set({ items: [] }),
      // Called by Wishlist page after fetching from server — syncs local state
      // without triggering API calls (server is already the source)
      syncFromServer: (serverItems) => set({ items: serverItems }),
    }),
    { name: 'tonys-wishlist' }
  )
)

// ── Auth Store ─────────────────────────────────────────────────────
export const useAuthStore = create(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      customer: null,      // customer record from /customer/me
      canViewAlcohol: true, // defaults true; false only if customer.list_liquor === false

      login: (user, token) => {
        set({ user, token, isAuthenticated: true })
        useCartStore.getState().claimCart(user.id)
      },
      setCustomer: (customer) => set({
        customer,
        canViewAlcohol: customer ? (customer.list_liquor ?? true) : true,
      }),
      logout: () => {
        set({ user: null, token: null, isAuthenticated: false, customer: null, canViewAlcohol: true })
        useCartStore.getState().clearCart()
      },
      updateUser: (data) => set((s) => ({ user: { ...s.user, ...data } })),
    }),
    { name: 'tonys-auth' }
  )
)

// ── Theme Store ────────────────────────────────────────────────────
// Persisted — applies html.pwa-light class for light mode
export const useThemeStore = create(
  persist(
    (set, get) => ({
      isDark: true,
      toggle: () => {
        const next = !get().isDark
        set({ isDark: next })
        if (next) {
          document.documentElement.classList.remove('pwa-light')
        } else {
          document.documentElement.classList.add('pwa-light')
        }
      },
      apply: () => {
        // Called on mount to re-apply persisted preference
        if (!get().isDark) {
          document.documentElement.classList.add('pwa-light')
        } else {
          document.documentElement.classList.remove('pwa-light')
        }
      },
    }),
    { name: 'tonys-pwa-theme' }
  )
)

// ── UI/App Store ───────────────────────────────────────────────────
export const useAppStore = create((set) => ({
  searchQuery: '',
  activeCategory: null,
  setSearch: (q) => set({ searchQuery: q }),
  setCategory: (c) => set({ activeCategory: c }),

  installPrompt: null,
  setInstallPrompt: (p) => set({ installPrompt: p }),

  isOnline: navigator.onLine,
  setOnline: (v) => set({ isOnline: v }),
}))
